import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CardWrapper,
  CardHeader,
  TitleBar,
  CardDescription,
  CardBottomSection,
  TabDisplay,
  DropButton,
  TabSection,
  AddIconDiv
} from './style';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import ButtonV2 from '../button/buttonV2';
import { rootStore } from '../../mobx/rootStore';
import FilterDropdowns from './FilterDropdowns';
import { Dropdown } from '../Dropdown';
import AddIconDropdown from './addIconDropdown';
import DisplayFilterDropdown from '../displayFilterDropdown/displayFilterDropdown';
import { observer } from 'mobx-react-lite';
import { dispatchProjectError } from '../../services/projectServices';
import FilterChips from './FilterChips';
import { createNewView, dispatchViewSuccess, getViewList, updateView } from '../../services/viewServices';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { VIEWS_FILTER_TYPE } from '../../mobx/interfaces/view';
import { updateWorkspaceViewPreference } from '../../helpers/firebaseHelper';
import { nanoid } from 'nanoid';
import { UserModel } from '../../mobx/models/user';
import { BUTTON_TYPE } from '../../global/constants';

const ViewCard: React.FC = () => {
  //State variables
  const [isEditMode, setIsEditMode] = useState(true);
  const [loading, setLoading] = useState(false);

  //Mobx store variables
  const { viewStore, workspaceStore } = rootStore;
  const { createViewInput, currentView } = viewStore;
  const { currentWorkspace } = workspaceStore;
  const { viewId } = useParams<{ viewId: string }>();

  //other variables
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const isEdit = useMemo(
    () => new URLSearchParams(location.search).get('isEdit') === 'true' || viewId === 'new-view',
    [location.search, viewId]
  );

  const loadProjectData = useCallback(async () => {
    if (!viewId || !currentWorkspace?.id) return;
    viewStore.setCurrentView(viewId);
    if (currentView && isEdit && viewId !== 'new-view') {
      createViewInput.updateWith(currentView);
    } else {
      createViewInput.setId(nanoid());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewId, currentWorkspace?.id, viewStore, currentView, isEdit]);

  useEffect(() => {
    loadProjectData();
    return () => {
      createViewInput.clear();
    };
  }, [loadProjectData, createViewInput]);

  const isFilterChanged = useCallback(() => {
    const isData = Object.values(createViewInput.filters || {}).some((filter) => {
      const firstValue = Object.values(filter)[0];
      return Array.isArray(firstValue) ? firstValue.length > 0 : !!firstValue;
    });
    setIsEditMode(isData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    createViewInput.filters.assign,
    createViewInput.filters.status,
    createViewInput.filters.priority,
    createViewInput.filters.project,
    createViewInput.filters.dueDate
  ]);

  useEffect(() => {
    isFilterChanged();
  }, [isFilterChanged]);

  const onCreateView = useCallback(async () => {
    try {
      setLoading(true);
      if (!createViewInput.name) {
        dispatchProjectError('Please enter a name for the view');
        return;
      }
      const payload = {
        name: createViewInput.name,
        description: createViewInput.description || createViewInput.generatedDescription,
        id: createViewInput.id,
        icon: createViewInput.icon,
        color: createViewInput.color,
        userIds: createViewInput.userIds,
        filters: createViewInput?.filters
      };

      const response = await dispatch(createNewView(payload));
      if (response) {
        dispatchViewSuccess('View created successfully');
        await updateWorkspaceViewPreference(response?.['_id'], { ...createViewInput?.displayFilters });
        await getViewList();
        history.push(`/workspace/${currentWorkspace?.id}/view/${response?.['_id']}`);
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      createViewInput?.clear();
      setLoading(false);
    }
  }, [createViewInput, currentWorkspace?.id, dispatch, history]);

  const onChangeAssign = useCallback(
    (user: UserModel) => {
      const isChecked = createViewInput?.filters?.assign?.assignees.includes(user.id);
      let updatedAssign = createViewInput?.filters?.assign?.assignees;
      if (isChecked) {
        updatedAssign = updatedAssign.filter((assign) => assign !== user.id);
      } else {
        updatedAssign = [...updatedAssign, user.id];
      }
      createViewInput.setAssignFilter(updatedAssign, createViewInput?.filters?.assign?.type);
    },
    [createViewInput]
  );

  const onChangeStatus = useCallback(
    (type: number) => {
      const isChecked = createViewInput?.filters?.status?.statuses.includes(type);
      let updatedStatus = createViewInput?.filters?.status?.statuses;
      if (isChecked) {
        updatedStatus = updatedStatus.filter((status) => status !== type);
      } else {
        updatedStatus = [...updatedStatus, type];
      }
      createViewInput.setStatusFilter(updatedStatus, createViewInput?.filters?.status?.type);
    },
    [createViewInput]
  );

  const onChangePriority = useCallback(
    (type: number) => {
      const isChecked = createViewInput?.filters?.priority?.priorities.includes(type);
      let updatedPriority = createViewInput?.filters?.priority?.priorities;
      if (isChecked) {
        updatedPriority = updatedPriority.filter((priority) => priority !== type);
      } else {
        updatedPriority = [...updatedPriority, type];
      }
      createViewInput.setPriorityFilter(updatedPriority, createViewInput?.filters?.priority?.type);
    },
    [createViewInput]
  );

  const onChangeProject = useCallback(
    (id: string) => {
      const isChecked = createViewInput?.filters?.project?.projects.includes(id);
      let updatedProject = createViewInput?.filters?.project?.projects;
      if (isChecked) {
        updatedProject = updatedProject.filter((project) => project !== id);
      } else {
        updatedProject = [...updatedProject, id];
      }
      createViewInput.setProjectFilter(updatedProject, createViewInput?.filters?.project?.type);
    },
    [createViewInput]
  );

  const onChangeDueDate = useCallback(
    (date: string) => {
      if (date === 'In the past' || date === 'No due date') {
        if (
          createViewInput?.filters?.dueDate?.type === VIEWS_FILTER_TYPE.IS ||
          createViewInput?.filters?.dueDate?.type === VIEWS_FILTER_TYPE.IS_NOT
        ) {
          createViewInput.setDueDateFilter([date], createViewInput?.filters?.dueDate?.type);
        } else {
          createViewInput.setDueDateFilter([date], VIEWS_FILTER_TYPE.IS);
        }
      } else {
        if (
          createViewInput?.filters?.dueDate?.type === VIEWS_FILTER_TYPE.IS ||
          createViewInput?.filters?.dueDate?.type === VIEWS_FILTER_TYPE.IS_NOT
        ) {
          createViewInput.setDueDateFilter([date], VIEWS_FILTER_TYPE.AFTER);
        } else {
          createViewInput.setDueDateFilter([date], createViewInput?.filters?.dueDate?.type);
        }
      }
    },
    [createViewInput]
  );

  const onUpdateView = useCallback(async () => {
    try {
      setLoading(true);
      if (!createViewInput.name) {
        dispatchProjectError('Please enter a name for the view');
        return;
      }
      const payload = {
        name: createViewInput.name,
        description: createViewInput.description || createViewInput.generatedDescription,
        id: createViewInput.id,
        icon: createViewInput.icon,
        color: createViewInput.color,
        userIds: createViewInput.userIds,
        filters: createViewInput?.filters
      };

      const response = await dispatch(updateView(payload, viewId));
      if (response) {
        await updateWorkspaceViewPreference(viewId, payload);
        await history.push(`/workspace/${currentWorkspace?.id}/view/${viewId}`);
        createViewInput.clear();
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      setLoading(false);
    }
  }, [createViewInput, currentWorkspace?.id, dispatch, history, viewId]);

  const handleCancel = useCallback(() => {
    createViewInput.clear();
    if (viewId === 'new-view') {
      history.push(`/views`);
    } else {
      history.push(`/workspace/${currentWorkspace?.id}/view/${viewId}`);
    }
  }, [createViewInput, currentWorkspace?.id, history, viewId]);

  return (
    <CardWrapper>
      <CardHeader>
        <TitleBar>
          <AddIconDiv>
            <AddIconDropdown />
          </AddIconDiv>

          {/* set edit condition */}
          {/* {createViewInput?.name ? ( */}
          <input
            type='text'
            value={createViewInput?.name}
            onChange={(e) => createViewInput?.setName(e.target.value)}
            placeholder='Title'
          />
          {/* // ) : (
          //   <CardTitle>{createViewInput?.name || 'Untitled View'}</CardTitle>
          // )} */}
          {viewId && viewId !== 'new-view' ? (
              <>
              <ButtonV2 title='Cancel' variant={BUTTON_TYPE.SECONDARY} onClick={handleCancel} size='small' />
              <ButtonV2
                title='Save'
                variant={BUTTON_TYPE.PRIMARY}
                onClick={onUpdateView}
                isLoading={loading}
                size='small'
              />
            </>
          ) : (
            <ButtonV2
              title='Create view'
              variant={BUTTON_TYPE.PRIMARY}
              onClick={onCreateView}
              isLoading={loading}
              size='small'
            />
          )}
        </TitleBar>
        <CardDescription>
          {true ? (
            <input
              type='text'
              value={createViewInput?.description}
              onChange={(e) => createViewInput?.setDescription(e.target.value)}
              placeholder={createViewInput?.generatedDescription || 'Description (optional)'}
            />
          ) : (
            <span>{createViewInput?.description || 'No description provided.'}</span>
          )}
        </CardDescription>
      </CardHeader>
      <CardBottomSection>
        <TabDisplay>
          <FilterChips
            onChangePriority={onChangePriority}
            onChangeStatus={onChangeStatus}
            onChangeProject={onChangeProject}
            onChangeDueDate={onChangeDueDate}
            onChangeAssign={onChangeAssign}
          />
          <TabSection>
            <Dropdown
              modalTitle='Filters'
              isMinWidth={150}
              content={
                <DropButton>
                  <SVGIcon name='small-filters-icon' width='16' height='16' viewBox='0 0 14 14' />
                  {!isEditMode && <span>Filters</span>}
                </DropButton>
              }
              trigger='click'>
              <FilterDropdowns
                onChangeStatus={onChangeStatus}
                onChangePriority={onChangePriority}
                onChangeProject={onChangeProject}
                onChangeDueDate={onChangeDueDate}
              />
            </Dropdown>
          </TabSection>
        </TabDisplay>

        <DisplayFilterDropdown />
      </CardBottomSection>
    </CardWrapper>
  );
};

export default observer(ViewCard);
